import PropTypes from "prop-types";
import { memo, useEffect } from "react";
import { useIntl } from "react-intl";
import "./booking-header.scss";
import { matchPath, useLocation } from "react-router-dom";
import { isServerSide } from "app/utils/utils";
import useHidePartnerCustomHeader from "app/utils/hooks/useHidePartnerCustomHeader";
import HeaderContainer from "app/pages/Header/HeaderContainer";
import PriceChangedWarningMessage from "app/pages/Booking/Payment/PriceChangedWarningMessage";
import { bookingPropTypes, quotationPropTypes } from "app/utils/propTypes";
import { TRANSPORTATION_TYPES, RESOLUTION } from "app/constants";
import { checkStepFromCurrentPath } from "app/pages/Stepper/StepperUtils";
import Stepper from "app/pages/.shared/Stepper/Stepper";

const BookingHeader = ({
	resolution,
	ficheProduitUri,
	hideHeader,
	booking = {},
	quotation = {},
	priceChangeData = {},
}) => {
	const location = useLocation();
	const { formatMessage } = useIntl();

	useHidePartnerCustomHeader({ hide: true });

	useEffect(() => {
		// hide header for SP
		if (!isServerSide && window.popupBridge && window.popupBridge.open) {
			const bookingHeader = document.getElementsByClassName("simple-header")[0];

			if (bookingHeader) {
				bookingHeader.style.display = "none";
			}
		}
	}, [location.pathname]);

	const currentPathname = location.pathname;
	const isBookingAuthPage = matchPath({ path: "/booking/auth" }, currentPathname);
	const isBookingSearchPage = matchPath({ path: "/booking/search" }, currentPathname);
	const isQuotationPage = matchPath({ path: "/booking/quote" }, currentPathname);
	const isConfirmationPage = currentPathname.includes("/booking/confirmation");

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const STEPS = [
		{
			value: 1,
			paths: ["/booking/search", ...(ficheProduitUri ? ["/" + ficheProduitUri] : [])],
		},
		{ value: 2, paths: ["/booking/quote"] },
		{ value: 3, paths: ["/booking/payment"] },
		{ value: 4, paths: ["/booking/confirmation"] },
	];
	const stepLabels = STEPS.map(step =>
		formatMessage({ id: `booking.breadcrumb.step.${step.value}` })
	);
	const currentStep = checkStepFromCurrentPath(STEPS, currentPathname);
	const hideStepper = isMobile && isBookingSearchPage;
	return (
		<div>
			<header className="booking-header" data-testid="booking-header">
				{!hideHeader && (
					<HeaderContainer
						showMenuItems={false}
						showDialPhone={true}
						showBackBtn={!isConfirmationPage}
					/>
				)}
				{!hideStepper && (
					<div className="booking-header__stepper">
						<Stepper steps={stepLabels} currentStep={currentStep} />
					</div>
				)}
				{isMobile &&
					priceChangeData?.isPriceChange &&
					!isBookingAuthPage &&
					!isBookingSearchPage && (
						<PriceChangedWarningMessage
							offerType={booking?.offer?.type}
							newPrice={quotation?.priceChange?.newPrice}
							oldPrice={quotation?.priceChange?.oldPrice}
							isTravelingByTrain={
								booking?.flight?.type === TRANSPORTATION_TYPES.TRAIN
							}
							{...(isQuotationPage && {
								messageFlightAccommodationChangeUp:
									"error.quotation.price.changed.up.message",
								messageFlightAccommodationChangeDown:
									"error.quotation.price.changed.down.message",
								messageAccommodationOnlyChangeUp:
									"error.quotation.price.changed.up.message",
								messageAccommodationOnlyChangeDown:
									"error.quotation.price.changed.down.message",
							})}
						/>
					)}
			</header>
		</div>
	);
};

BookingHeader.propTypes = {
	resolution: PropTypes.string,
	hideHeader: PropTypes.bool,
	ficheProduitUri: PropTypes.string,
	quotation: quotationPropTypes,
	booking: bookingPropTypes,
	priceChangeData: PropTypes.object,
};

export default memo(BookingHeader);
